import { useState, useEffect } from "react";
import DashboardMapWidget from "./DashboardMapWidget";
import { aiApi } from "./services/aiApi";

const BASE = "https://support.gs-group.fr/api";
const h = () => ({ "Content-Type":"application/json", Authorization:"Bearer "+localStorage.getItem("nexo_token") });

const PRIO_C = { urgent:"#ef4444",urgente:"#ef4444",high:"#f59e0b",haute:"#f59e0b",medium:"#6366f1",normale:"#6366f1",low:"#22d3a0",basse:"#22d3a0" };

const fmtH = (h) => { const hh=Math.floor(h), mm=Math.round((h-hh)*60); return `${hh}h${mm>0?mm.toString().padStart(2,"0"):""}`; };
const MOIS = ["Jan","Fév","Mar","Avr","Mai","Juin","Juil","Août","Sep","Oct","Nov","Déc"];

// ── Chronomètre live ──────────────────────────────────────────────
function LiveChrono({ startedAt, color }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!startedAt) return;
    const calc = () => setElapsed(Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000));
    calc();
    const t = setInterval(calc, 1000);
    return () => clearInterval(t);
  }, [startedAt]);
  if (!startedAt) return null;
  const h2 = Math.floor(elapsed / 3600), m = Math.floor((elapsed % 3600) / 60), s = elapsed % 60;
  const warn = elapsed > 14400; // > 4h
  return (
    <span style={{ fontFamily:"monospace", fontSize:13, fontWeight:800, color: warn ? "#ef4444" : color||"#6366f1", letterSpacing:"0.5px" }}>
      {String(h2).padStart(2,"0")}:{String(m).padStart(2,"0")}:{String(s).padStart(2,"0")}
    </span>
  );
}

// ── Card wrapper ──────────────────────────────────────────────────
function Card({ T, children, style={} }) {
  return <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:14, overflow:"hidden", ...style }}>{children}</div>;
}
function CardH({ T, label, right }) {
  return (
    <div style={{ padding:"12px 18px", borderBottom:`1px solid ${T.border}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
      <span style={{ fontSize:11, fontWeight:800, color:T.muted, textTransform:"uppercase", letterSpacing:"1px" }}>{label}</span>
      {right}
    </div>
  );
}

// ── KPIs ──────────────────────────────────────────────────────────
function KPIRow({ T, active, techActifs, totalSupMois, alertSto, setView }) {
  const KPIS = [
    { ic:"⚙️", val:active.length, lbl:"Interventions actives", col:T.accent, bg:T.accentDim, pulse:active.length>0, onClick:()=>setView("pipeline") },
    { ic:"🧑‍🔧", val:techActifs.length, lbl:"Techniciens en mission", col:T.success, bg:T.successDim },
    { ic:"⚡", val:fmtH(totalSupMois), lbl:"Heures sup ce mois", col:T.danger, bg:T.dangerDim, onClick:()=>setView("heures") },
    { ic:"📦", val:alertSto.length, lbl:"Alertes stock", col:T.warning, bg:T.warningDim, pulse:alertSto.length>0, onClick:()=>setView("stock") },
  ];
  return (
    <div className="dash-kpis">
      {KPIS.map((k,ki) => (
        <div key={ki} onClick={k.onClick} style={{ background:k.bg, borderRadius:14, padding:"18px 20px", display:"flex", alignItems:"center", gap:14, cursor:k.onClick?"pointer":"default", transition:"all 0.15s", border:`1px solid ${k.col}18` }}
          onMouseEnter={e=>{ if(k.onClick){ e.currentTarget.style.transform="translateY(-2px)"; e.currentTarget.style.boxShadow=`0 8px 24px ${k.col}18`; } }}
          onMouseLeave={e=>{ e.currentTarget.style.transform="translateY(0)"; e.currentTarget.style.boxShadow="none"; }}>
          <div style={{ width:46, height:46, borderRadius:13, background:`${k.col}20`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, flexShrink:0, position:"relative" }}>
            {k.ic}
            {k.pulse && <div style={{ position:"absolute", top:2, right:2, width:8, height:8, borderRadius:"50%", background:k.col, animation:"kpi-pulse 1.5s infinite" }}/>}
          </div>
          <div>
            <div style={{ fontSize:28, fontWeight:900, color:k.col, letterSpacing:"-1px", lineHeight:1 }}>{k.val}</div>
            <div style={{ fontSize:11, color:T.muted, marginTop:4 }}>{k.lbl}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Interventions en cours (live) ──────────────────────────────────
function SectionActiveInterventions({ T, active, setSelInt }) {
  return (
    <Card T={T}>
      <CardH T={T} label={`⚙️ Interventions en cours (${active.length})`} right={
        <span style={{ fontSize:10, color:T.accent, background:T.accentDim, padding:"3px 10px", borderRadius:99, fontWeight:700 }}>🔴 Live</span>
      }/>
      {active.length === 0
        ? <div style={{ padding:"28px 20px", textAlign:"center", color:T.muted, fontSize:13 }}>Aucune intervention active en ce moment</div>
        : active.map((i, idx) => {
          const elapsed = i.started_at ? Math.floor((Date.now()-new Date(i.started_at).getTime())/3600000) : 0;
          const warn = elapsed >= 4;
          return (
            <div key={i.id} onClick={() => setSelInt(i)} style={{ display:"flex", alignItems:"center", gap:14, padding:"13px 18px", borderBottom: idx < active.length-1 ? `1px solid ${T.border}` : undefined, cursor:"pointer", transition:"background 0.1s", animation:`row-in 0.3s ease ${idx*0.05}s both` }}
              onMouseEnter={e => e.currentTarget.style.background=T.bg}
              onMouseLeave={e => e.currentTarget.style.background="transparent"}>
              <div style={{ width:10, height:10, borderRadius:"50%", background:T.accent, animation:"kpi-pulse 2s infinite", flexShrink:0 }}/>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:13, fontWeight:700, color:T.text, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{i.titre}</div>
                <div style={{ fontSize:11, color:T.muted, marginTop:2 }}>
                  {i.client_nom||"—"} · {i.technicien_nom||"Non assigné"}
                </div>
              </div>
              <div style={{ textAlign:"right", flexShrink:0 }}>
                <LiveChrono startedAt={i.started_at} color={T.accent}/>
                {warn && <div style={{ fontSize:9, color:T.danger, fontWeight:700, marginTop:2 }}>⚠ +4h</div>}
              </div>
              {i.priorite && <span style={{ fontSize:10, fontWeight:700, color:PRIO_C[i.priorite]||T.muted, background:`${PRIO_C[i.priorite]||T.muted}18`, padding:"2px 8px", borderRadius:99, flexShrink:0 }}>{i.priorite}</span>}
            </div>
          );
        })
      }
    </Card>
  );
}

// ── Prochaines interventions planifiées ────────────────────────────
function SectionPlanifiees({ T, planif, setSelInt, setView }) {
  if (planif.length === 0) return null;
  const now = new Date();
  return (
    <Card T={T}>
      <CardH T={T} label={`📅 Planifiées (${planif.length})`} right={
        <button onClick={() => setView("planning")} style={{ fontSize:10, color:T.accent, background:T.accentDim, border:"none", borderRadius:6, padding:"3px 9px", cursor:"pointer", fontWeight:700 }}>Planning →</button>
      }/>
      <div style={{ display:"flex", flexDirection:"column" }}>
        {planif.slice(0,5).map((i, idx) => {
          const d = new Date(i.date_planifiee||i.created_at);
          const isToday = d.toDateString() === now.toDateString();
          const isTomorrow = d.toDateString() === new Date(now.getTime()+86400000).toDateString();
          const dateLbl = isToday ? "Aujourd'hui" : isTomorrow ? "Demain" : d.toLocaleDateString("fr-FR",{weekday:"short",day:"numeric",month:"short"});
          return (
            <div key={i.id} onClick={() => setSelInt(i)} style={{ display:"flex", alignItems:"center", gap:14, padding:"10px 18px", borderBottom: idx<planif.slice(0,5).length-1 ? `1px solid ${T.border}20`:undefined, cursor:"pointer", transition:"background 0.1s" }}
              onMouseEnter={e => e.currentTarget.style.background=T.bg}
              onMouseLeave={e => e.currentTarget.style.background="transparent"}>
              <div style={{ width:42, height:42, borderRadius:11, background:isToday?T.accentDim:T.bg, border:`1px solid ${isToday?T.accent+"30":T.border}`, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                <div style={{ fontSize:14, fontWeight:800, color:isToday?T.accent:T.text, lineHeight:1 }}>{d.getDate()}</div>
                <div style={{ fontSize:9, color:T.muted, textTransform:"uppercase" }}>{MOIS[d.getMonth()]}</div>
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:13, fontWeight:600, color:T.text, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{i.titre}</div>
                <div style={{ fontSize:11, color:T.muted, marginTop:2 }}>{i.client_nom||"—"} · {i.technicien_nom||"Non assigné"}</div>
              </div>
              <span style={{ fontSize:11, fontWeight:700, color:isToday?T.accent:T.muted, flexShrink:0 }}>{dateLbl}</span>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ── Techniciens du jour ─────────────────────────────────────────────
function SectionTechniciens({ T, techniciens, interventions, heuresDash }) {
  const COLORS = [T.accent, T.success, T.warning, T.purple, T.danger, T.info];
  return (
    <Card T={T}>
      <CardH T={T} label="🧑‍🔧 Techniciens — Aujourd'hui"/>
      <div style={{ padding:"10px 16px", display:"flex", flexDirection:"column", gap:8 }}>
        {techniciens.length === 0
          ? <div style={{ textAlign:"center", padding:"20px", color:T.muted, fontSize:12 }}>Aucun technicien</div>
          : techniciens.slice(0,6).map((t, ti) => {
            const col = COLORS[ti % COLORS.length];
            const myActive = interventions.filter(i => i.technicien_id===t.id && ["en_cours","in-progress"].includes(i.statut));
            const myPlanif = interventions.filter(i => i.technicien_id===t.id && ["planifiee","pending"].includes(i.statut));
            const myDone   = interventions.filter(i => i.technicien_id===t.id && ["terminee","completed"].includes(i.statut));
            const hrRow    = heuresDash.find(hd => hd.id === t.id);
            return (
              <div key={t.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 10px", borderRadius:10, background:myActive.length>0?`${col}08`:"transparent", border:`1px solid ${myActive.length>0?col+"30":T.border}`, transition:"all 0.15s" }}>
                <div style={{ width:34, height:34, borderRadius:"50%", background:`${col}20`, border:`2px solid ${myActive.length>0?col:col+"40"}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:800, color:col, flexShrink:0 }}>
                  {(t.prenom?.[0]||"")+(t.nom?.[0]||"")}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:12, fontWeight:700, color:T.text, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                    {t.prenom} {t.nom}
                    {myActive.length>0 && <span style={{ marginLeft:6, fontSize:9, fontWeight:800, color:col, background:`${col}20`, padding:"1px 6px", borderRadius:99 }}>EN COURS</span>}
                  </div>
                  <div style={{ fontSize:10, color:T.muted }}>
                    {myActive.length>0 && myActive[0].titre ? `⚙ ${myActive[0].titre}` : myPlanif.length>0 ? `📅 ${myPlanif.length} planif.` : myDone.length>0 ? `✅ ${myDone.length} terminée(s)` : "Disponible"}
                  </div>
                </div>
                {hrRow && parseFloat(hrRow.heures_realisees) > 0 && (
                  <span style={{ fontSize:10, color:T.muted, flexShrink:0 }}>{fmtH(parseFloat(hrRow.heures_realisees))}</span>
                )}
              </div>
            );
          })
        }
      </div>
    </Card>
  );
}

// ── Résumé heures du mois (condensé, renvoie vers /heures) ──────────
function SectionHeuresSummary({ T, heuresDash, setView }) {
  const now = new Date();
  const totalReal = heuresDash.reduce((s,t)=>s+(parseFloat(t.heures_realisees)||0),0);
  const totalSup  = heuresDash.reduce((s,t)=>s+(parseFloat(t.heures_sup)||0),0);
  const aValider  = heuresDash.reduce((s,t)=>s+(parseInt(t.a_valider)||0),0);
  return (
    <Card T={T}>
      <CardH T={T} label={`⏱️ Heures — ${MOIS[now.getMonth()]}`} right={
        <button onClick={() => setView("heures")} style={{ fontSize:10, color:T.accent, background:T.accentDim, border:"none", borderRadius:6, padding:"3px 9px", cursor:"pointer", fontWeight:700 }}>Détail →</button>
      }/>
      <div style={{ padding:"14px 18px", display:"flex", flexDirection:"column", gap:10 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <span style={{ fontSize:12, color:T.muted }}>Total réalisé</span>
          <span style={{ fontSize:16, fontWeight:800, color:T.text }}>{fmtH(totalReal)}</span>
        </div>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <span style={{ fontSize:12, color:T.muted }}>Heures sup</span>
          <span style={{ fontSize:13, fontWeight:700, color:totalSup>0?T.danger:T.muted }}>{totalSup>0?"+"+fmtH(totalSup):"—"}</span>
        </div>
        {aValider > 0 && (
          <div style={{ marginTop:2, padding:"9px 12px", borderRadius:9, background:T.warningDim, border:`1px solid ${T.warning}40`, fontSize:12, color:T.warning, fontWeight:700 }}>
            ⏳ {aValider} feuille{aValider>1?"s":""} en attente
          </div>
        )}
      </div>
    </Card>
  );
}

// ── Résumé stock (condensé, renvoie vers /stock) ─────────────────────
function SectionStockSummary({ T, alertSto, setView }) {
  return (
    <Card T={T}>
      <CardH T={T} label={`📦 Alertes stock (${alertSto.length})`} right={
        <button onClick={() => setView("stock")} style={{ fontSize:10, color:T.accent, background:T.accentDim, border:"none", borderRadius:6, padding:"3px 9px", cursor:"pointer", fontWeight:700 }}>Voir tout →</button>
      }/>
      {alertSto.length === 0
        ? <div style={{ padding:"20px", textAlign:"center", color:T.muted, fontSize:12 }}>✅ Tous les stocks sont OK</div>
        : <div style={{ display:"flex", flexDirection:"column" }}>
            {alertSto.slice(0,4).map((s, idx) => (
              <div key={s.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 18px", borderBottom: idx<Math.min(alertSto.length,4)-1?`1px solid ${T.border}20`:undefined }}>
                <div style={{ flex:1, minWidth:0, fontSize:12, color:T.text, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{s.nom||s.designation||s.reference}</div>
                <span style={{ fontSize:11, fontWeight:800, color:T.warning }}>{s.quantite}</span>
              </div>
            ))}
          </div>
      }
    </Card>
  );
}

// ── Composant principal ───────────────────────────────────────────
export default function DashboardNew({ interventions=[], tickets=[], techniciens=[], stock=[], user, setView, onNewInt, onNewSAV, setSelInt, T={}, echecAlerts=[], onClearEchec }) {
  const [heuresDash, setHeuresDash] = useState([]);
  const [aiSummary, setAiSummary] = useState(null); // {loading, error, resume, stats}
  const now = new Date();

  useEffect(() => {
    fetch(`${BASE}/heures/dashboard?mois=${now.getMonth()+1}&annee=${now.getFullYear()}`, { headers: h() })
      .then(r => r.json()).then(d => { if (Array.isArray(d)) setHeuresDash(d); }).catch(() => {});
  }, []);

  useEffect(() => {
    setAiSummary({ loading: true, error: null, resume: "", stats: null });
    aiApi.dashboardSummary()
      .then(d => setAiSummary({ loading: false, error: null, ...d }))
      .catch(e => setAiSummary({ loading: false, error: e.message, resume: "", stats: null }));
  }, []);

  const active   = interventions.filter(i => ["en_cours","in-progress"].includes(i.statut));
  const planif   = interventions.filter(i => ["planifiee","pending"].includes(i.statut));
  const alertSto = stock.filter(s => s.quantite <= (s.quantite_min || 1));
  const techActifs = techniciens.filter(t => interventions.some(i => i.technicien_id === t.id && ["en_cours","in-progress"].includes(i.statut)));
  const totalSupMois = heuresDash.reduce((s, t) => s + (parseFloat(t.heures_sup) || 0), 0);
  const aReplanifier = interventions.filter(i=>i.statut==="a_replanifier").length;

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:18 }}>
      <style>{`
        @keyframes fadeIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}
        @keyframes kpi-pulse{0%,100%{opacity:1}50%{opacity:0.3}}
        @keyframes row-in{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}
        @keyframes echec-blink{0%,100%{opacity:1;box-shadow:0 0 0 3px rgba(249,115,22,0.2)}50%{opacity:0.85;box-shadow:0 0 0 6px rgba(249,115,22,0.08)}}
        @keyframes echec-badge{0%,100%{transform:scale(1)}50%{transform:scale(1.07)}}
        .dash-kpis { display:grid; grid-template-columns:repeat(4,1fr); gap:12px; }
        .dash-main-grid { display:grid; grid-template-columns:2fr 1fr; gap:16px; align-items:start; }
        @media (max-width:1024px) { .dash-main-grid { grid-template-columns:1fr; } }
        @media (max-width:768px) { .dash-kpis { grid-template-columns:repeat(2,1fr); } }
      `}</style>

      {/* ── ALERTES INTERVENTIONS ÉCHOUÉES (SSE temps réel) ── */}
      {echecAlerts.length > 0 && (
        <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
          {echecAlerts.map(a => (
            <div key={a.id} style={{ background:"linear-gradient(90deg,#fff7ed,#fef3c7)", border:"2px solid #f97316", borderRadius:14, padding:"13px 18px", display:"flex", alignItems:"center", gap:12, animation:"echec-blink 2s infinite" }}>
              <div style={{ fontSize:24, animation:"echec-badge 1.5s infinite" }}>🔶</div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:14, fontWeight:800, color:"#c2410c" }}>
                  Intervention échouée chez {a.client_nom || "—"}
                </div>
                <div style={{ fontSize:12, color:"#92400e", marginTop:3 }}>
                  {a.tech_nom && <span>👷 {a.tech_nom} · </span>}
                  Motif : <em>{a.motif || "Non précisé"}</em> · Ticket placé en <strong>À re-planifier</strong>
                </div>
              </div>
              <div style={{ display:"flex", gap:8, alignItems:"center", flexShrink:0 }}>
                <button onClick={()=>setView("pipeline")}
                  style={{ padding:"8px 14px", borderRadius:10, background:"#f97316", border:"none", color:"#fff", fontSize:12, fontWeight:700, cursor:"pointer" }}>
                  Voir pipeline →
                </button>
                <button onClick={()=>onClearEchec&&onClearEchec(a.id)}
                  style={{ padding:"8px", borderRadius:10, background:"#fed7aa", border:"none", color:"#92400e", fontSize:14, fontWeight:700, cursor:"pointer", lineHeight:1 }}
                  title="Masquer cette alerte">×</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── RAPPEL VISUEL tickets à re-planifier ── */}
      {aReplanifier > 0 && echecAlerts.length === 0 && (
        <div style={{ background:"#fff7ed", border:"1.5px solid #f97316", borderRadius:12, padding:"10px 16px", display:"flex", alignItems:"center", gap:10 }}>
          <span style={{ fontSize:16 }}>🔶</span>
          <span style={{ fontSize:13, fontWeight:700, color:"#c2410c" }}>{aReplanifier} intervention{aReplanifier>1?"s":""} en attente de re-planification</span>
          <button onClick={()=>setView("pipeline")} style={{ marginLeft:"auto", padding:"6px 14px", borderRadius:9, background:"#f97316", border:"none", color:"#fff", fontSize:12, fontWeight:700, cursor:"pointer" }}>Voir →</button>
        </div>
      )}

      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexWrap:"wrap", gap:12 }}>
        <div>
          <div style={{ fontSize:24, fontWeight:900, color:T.text, letterSpacing:"-0.5px", lineHeight:1 }}>
            Bonjour, {user?.prenom} 👋
          </div>
          <div style={{ fontSize:13, color:T.muted, marginTop:6, display:"flex", alignItems:"center", gap:8 }}>
            <span style={{ display:"inline-block", width:7, height:7, borderRadius:"50%", background:T.success, boxShadow:`0 0 0 3px ${T.successDim}` }}/>
            {new Date().toLocaleDateString("fr-FR",{weekday:"long",day:"numeric",month:"long"})} · GS GROUP Support
          </div>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <button onClick={onNewInt} style={{ padding:"9px 18px", borderRadius:10, background:T.accent, border:"none", color:"#fff", fontSize:13, fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", gap:6 }}>
            <span style={{ fontSize:16 }}>＋</span> Intervention
          </button>
          <button onClick={onNewSAV} style={{ padding:"9px 16px", borderRadius:10, background:T.card, border:`1px solid ${T.border}`, color:T.text, fontSize:13, fontWeight:600, cursor:"pointer" }}>
            🎫 SAV
          </button>
          <button onClick={() => setView("pipeline")} style={{ padding:"9px 16px", borderRadius:10, background:T.card, border:`1px solid ${T.border}`, color:T.muted, fontSize:13, fontWeight:600, cursor:"pointer" }}>
            Pipeline →
          </button>
        </div>
      </div>

      {/* Résumé IA */}
      {aiSummary && (
        <div style={{ background:`linear-gradient(135deg,${T.accentDim},${T.card})`, border:`1px solid ${T.accent}30`, borderRadius:14, padding:"16px 20px", display:"flex", flexDirection:"column", gap:12 }}>
          <div style={{ display:"flex", gap:14, alignItems:"flex-start" }}>
            <div style={{ fontSize:22, flexShrink:0 }}>🤖</div>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:11, fontWeight:800, color:T.accent, textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:5 }}>Résumé du jour</div>
              {aiSummary.loading ? (
                <div style={{ fontSize:13, color:T.muted, fontStyle:"italic" }}>Analyse de l'activité en cours…</div>
              ) : aiSummary.error ? (
                <div style={{ fontSize:13, color:T.muted, fontStyle:"italic" }}>Résumé indisponible ({aiSummary.error})</div>
              ) : (
                <div style={{ fontSize:13.5, color:T.text, lineHeight:1.6 }}>{aiSummary.resume}</div>
              )}
            </div>
          </div>
          {!aiSummary.loading && aiSummary.stats && (
            <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
              {[
                ["✅ Réussies aujourd'hui", aiSummary.stats.reussies, T.success],
                ["🔶 Non résolues", aiSummary.stats.non_resolues, aiSummary.stats.non_resolues>0?"#f97316":T.muted],
                ["📅 Planifiées aujourd'hui", aiSummary.stats.planifiees_jour, T.accent],
                ["🚦 Risque retard trafic", aiSummary.stats.en_retard_trafic, aiSummary.stats.en_retard_trafic>0?"#ef4444":T.muted],
                ...(aiSummary.stats.alertes_flotte!=null ? [["🚗 Alertes flotte", aiSummary.stats.alertes_flotte, aiSummary.stats.alertes_flotte>0?"#ef4444":T.muted]] : []),
              ].map(([lbl,val,c])=>(
                <div key={lbl} style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:10, padding:"7px 12px", display:"flex", alignItems:"center", gap:7 }}>
                  <span style={{ fontSize:15, fontWeight:800, color:c }}>{val}</span>
                  <span style={{ fontSize:11, color:T.muted, fontWeight:600 }}>{lbl}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* KPIs */}
      <KPIRow T={T} active={active} techActifs={techActifs} totalSupMois={totalSupMois} alertSto={alertSto} setView={setView}/>

      {/* Carte + colonne principale / colonne latérale */}
      <div className="dash-main-grid">
        <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
          <DashboardMapWidget T={T} interventions={interventions} techniciens={techniciens} onExpand={() => setView("carte")}/>
          <SectionActiveInterventions T={T} active={active} setSelInt={setSelInt}/>
          <SectionPlanifiees T={T} planif={planif} setSelInt={setSelInt} setView={setView}/>
        </div>
        <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
          <SectionTechniciens T={T} techniciens={techniciens} interventions={interventions} heuresDash={heuresDash}/>
          <SectionHeuresSummary T={T} heuresDash={heuresDash} setView={setView}/>
          <SectionStockSummary T={T} alertSto={alertSto} setView={setView}/>
        </div>
      </div>
    </div>
  );
}
